import { useTranslation } from 'react-i18next';

import * as St from './OrderInvoice.styles';

import { useOrderInvoice } from '~/UI/pages/OrderSummaryPage/hooks';
import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { getSearchParameter } from '~/core/helpers';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { isDiscountOverrideEligible } from '~/core/models';
import { formatPriceUnitOrFree, formatPriceWithUnit } from '~/core/utils';
import AdditionalDiscountLine from '~atoms/AdditionalDiscountLine';
import Divider from '~atoms/Divider';
import InvoiceLine from '~atoms/InvoiceLine';
import InvoiceTable from '~atoms/InvoiceTable';

interface OrderInvoiceProps {
  tableMaxHeight?: string;
  showInstallationFee?: boolean;
  showAdditionalDiscount: boolean;
}

const OrderInvoice = ({
  tableMaxHeight,
  showInstallationFee,
  showAdditionalDiscount,
}: OrderInvoiceProps) => {
  const { t } = useTranslation();
  const {
    savings,
    promotionEndDate,
    subtotal,
    maximumDiscount,
    displayTotal,
    deliveryCost,
    discountOverride,
  } = useOrderInvoice();
  const installationFee = t('ORDER_SUMMARY.INVOICE.INCLUDED');
  const { retailerSettings } = useRetailerSettings();
  const userToken = getSearchParameter(LOCAL_STORAGE_KEY.USER_TOKEN);

  const hasDiscountOverride = isDiscountOverrideEligible(
    retailerSettings?.userOptions,
    userToken
  );

  const deliveryFee = t('ORDER_SUMMARY.INVOICE.INCLUDED');
  const promoLabel = promotionEndDate
    ? t('ORDER_SUMMARY.INVOICE.PROMOTION_END', { date: promotionEndDate })
    : undefined;

  return (
    <St.Container>
      <InvoiceTable maxHeight={tableMaxHeight} />

      <Divider m={[0, 0]} />

      <InvoiceLine
        label={`${t('ORDER_SUMMARY.INVOICE.SUBTOTAL')}:`}
        value={formatPriceUnitOrFree(subtotal)}
      />

      {savings ? (
        <InvoiceLine
          color="green"
          label={`${t('ORDER_SUMMARY.INVOICE.SAVINGS_INCLUDED')}:`}
          value={formatPriceUnitOrFree(savings)}
        />
      ) : null}

      {showAdditionalDiscount && hasDiscountOverride ? (
        <AdditionalDiscountLine
          maximumDiscount={maximumDiscount}
          discountOverride={discountOverride}
        />
      ) : null}

      <InvoiceLine
        label={`${t('ORDER_SUMMARY.INVOICE.DELIVERY')}:`}
        value={
          !!deliveryCost && deliveryCost > 0
            ? formatPriceWithUnit(deliveryCost)
            : deliveryFee
        }
      />

      {showInstallationFee && (
        <InvoiceLine
          label={`${t('ORDER_SUMMARY.INVOICE.COUNTERTOP_INSTALLATION')}:`}
          value={installationFee}
        />
      )}

      <Divider size="large" mb={0} />

      <InvoiceLine
        size="large"
        label={`${t('ORDER_SUMMARY.INVOICE.TOTAL')}:`}
        value={displayTotal}
      />

      <St.TaxStatement>{t('ORDER_SUMMARY.INVOICE.TAX')}</St.TaxStatement>

      {promoLabel && <InvoiceLine color="red" value={promoLabel} />}
    </St.Container>
  );
};

export default OrderInvoice;
